import type { ContentType } from "@brookbush/database";
import type { ContentItem } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import { Heart } from "lucide-react";
import { usePlausible } from "next-plausible";
import { toast } from "react-hot-toast";
import { FaHeart } from "react-icons/fa";
import useSWR from "swr";

import { showReferralToast } from "~/lib/toasts/showReferralToast";
import fetcher from "~/utils/fetcher.client";

type UseLikesProps =
	| {
			excludeContent?: boolean;
			contentType?: ContentType;
	  }
	| undefined;

const useLikes = ({ contentType, excludeContent }: UseLikesProps = {}) => {
	const { isSignedIn } = useAuth();
	const queryString = new URLSearchParams({
		...(contentType ? { contentType } : {}),
		...(excludeContent ? { exclude: "content" } : {}),
	}).toString();

	const track = usePlausible();

	const { mutate, ...rest } = useSWR<ContentItem[]>(
		isSignedIn && `/likes?${queryString}`,
	);

	const addLike = async (contentId: string) => {
		await mutate(
			fetcher(`/likes`, {
				method: "POST",
				data: {
					contentId,
					contentType,
				},
			}),

			{
				populateCache: (newData: ContentItem, oldData) => {
					toast.success("Added To Likes", {
						icon: <FaHeart size={22} />,
					});
					showReferralToast();
					return [...oldData!, newData];
				},
				revalidate: true,
			},
		);

		track("Like", {
			props: {
				contentType,
			},
		});
	};

	const removeLike = async (id: number) => {
		await mutate(
			fetcher(`/likes/${id}`, {
				method: "DELETE",
			}),

			{
				revalidate: false,
				populateCache: (newData: ContentItem, oldData) => {
					toast.success(`Removed From Likes`, {
						icon: <Heart size={24} />,
					});
					return oldData!.filter((like) => like.id !== newData.id);
				},
			},
		);
	};

	return {
		addLike,
		removeLike,
		likes: rest.data,
		isLoadingLikes: rest.isLoading,
		...rest,
	};
};

export default useLikes;
